import { StyleSheet } from 'react-native';

import type { useTheme } from '../theme';

// Reps/weight inputs, chips, add buttons, errors, cardio (Track R — split from loggerStyles.ts; pure data,
// merged by the loggerStyles barrel). Not imported directly.
export function inputStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    setInput: {
      // Reps cell. Width compressed to 56 to free pixels for the
      // weight cell when rpe/rir column is active (rep counts above
      // 99 are rare on weight training; 2-3 digits fit in 56). Height
      // pinned to 40 to match every other row interactive.
      width: 56,
      height: 40,
      backgroundColor: p.bg,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r2,
      color: p.ink,
      fontSize: t.sizes.base,
      paddingHorizontal: s.s2,
      paddingVertical: 0,
      textAlign: 'center',
      fontVariant: ['tabular-nums'],
    },
    // Timed (isometric) h/min/s cell. Narrow 2-digit box (a hold rarely
    // needs >99 of any unit) so the pill + 3 time cells + extra + actions
    // fit one row on a phone — the strength-table layout Antenore asked
    // for (column headers name h/min/s; the cells are bare numbers, no
    // inline suffix that would crowd the digit). Width matches colHour/
    // colMin/colSec so header + cells align top-to-bottom.
    timeInput: {
      width: 44,
      height: 40,
      backgroundColor: p.bg,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r2,
      color: p.ink,
      fontSize: t.sizes.base,
      paddingHorizontal: s.s1,
      paddingVertical: 0,
      textAlign: 'center',
      fontVariant: ['tabular-nums'],
    },
    // Fixed 3-digit width matching colWeight (header) + footerWeightCell
    // (footer) so the column aligns top-to-bottom. Was flex:1 — it grew
    // ~2× the reps field and pushed the amber assist border off-screen
    // (Antenore 2026-05-29, v2.58 smoke).
    weightCell: {
      width: 68,
      flexDirection: 'row',
      alignItems: 'stretch',
    },
    weightInput: {
      flex: 1,
      height: 40,
      backgroundColor: p.bg,
      borderColor: p.border,
      borderWidth: 1,
      // Full bordered box: the old right-edge-flush styling existed to butt
      // against a unit chip that no longer sits on the row (it moved to the
      // stepper bar). A complete border also lets the negative-extra amber
      // show on all four sides (D1 2026-05-29).
      borderRadius: r.r2,
      color: p.ink,
      fontSize: t.sizes.base,
      paddingHorizontal: s.s2,
      paddingVertical: 0,
      textAlign: 'center',
      fontVariant: ['tabular-nums'],
    },
    weightInputSquaredLeft: {
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
      borderLeftWidth: 0,
    },
    // Bodyweight assistance (negative extra) — the SIGN is shown as colour,
    // not a glyph, since there's no on-row ± control (space constraint, D1
    // 2026-05-29). Amber 'warn' (distinct from the green complete/gold PR
    // states). The field still holds the magnitude; this just recolours it.
    weightInputNegative: {
      color: p.warn,
      borderColor: p.warn,
    },
    unitChip: {
      paddingHorizontal: s.s2,
      borderColor: p.border,
      borderWidth: 1,
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
      borderRadius: r.r2,
      backgroundColor: p.surface,
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 36,
    },
    unitChipPressed: {
      backgroundColor: p.surface2,
    },
    unitChipLocked: {
      opacity: 0.5,
    },
    unitChipText: {
      color: p.ink2,
      fontSize: t.sizes.xs,
      fontWeight: '700',
      letterSpacing: 0.6,
    },
    setInputLocked: {
      backgroundColor: p.surface2,
      color: p.ink2,
    },
    // Inline per-field validation (2026-06-08): a malformed / out-of-range
    // number gets a danger border. NEVER colour alone — always paired with the
    // fieldError text line below (reduced-vision a11y rule).
    inputInvalid: {
      borderColor: p.danger,
      color: p.danger,
    },
    fieldError: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s1,
      paddingTop: s.s1,
      paddingLeft: 64,
    },
    fieldErrorText: {
      color: p.danger,
      fontSize: t.sizes.xs,
      fontWeight: '600',
    },
    addSet: {
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r2,
      paddingVertical: s.s3,
      alignItems: 'center',
      marginTop: s.s2,
    },
    addSetPressed: {
      backgroundColor: p.surface2,
    },
    addSetText: {
      color: p.ink2,
      fontSize: t.sizes.sm,
      fontWeight: '600',
    },
    // Cardio Logger UI (1.66.9 / refined 1.66.11). Single horizontal
    // row instead of the original vertical stack — the stacked layout
    // pushed siblings off the line on narrow screens. The mm:ss display
    // takes the visual weight, the seconds TextInput stays compact at
    // a fixed 80pt so [pill] [display] [input] [×] always fit on one
    // line.
    cardioDurationCell: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s2,
    },
    cardioDurationValue: {
      flex: 1,
      color: p.ink,
      fontSize: t.sizes.lg,
      fontWeight: '700',
      fontVariant: ['tabular-nums'],
    },
    cardioDurationInput: {
      width: 80,
      backgroundColor: p.surface2,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r2,
      color: p.ink,
      fontSize: t.sizes.base,
      paddingHorizontal: s.s2,
      paddingVertical: s.s1,
      minHeight: 40,
      textAlign: 'center',
    },
    cardioOptionalsToggle: {
      paddingLeft: 64,
      paddingVertical: s.s1,
    },
    cardioOptionalsTogglePressed: {
      opacity: 0.6,
    },
    cardioOptionalsToggleText: {
      color: p.ink2,
      fontSize: t.sizes.sm,
      fontWeight: '600',
    },
    cardioOptionals: {
      paddingLeft: 64,
      paddingRight: s.s2,
      gap: s.s2,
      paddingBottom: s.s2,
    },
    cardioOptionalRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s2,
    },
    cardioOptionalLabel: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      letterSpacing: 0.4,
      textTransform: 'uppercase',
      fontWeight: '700',
      width: 70,
    },
    cardioOptionalInput: {
      flex: 1,
      backgroundColor: p.surface2,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r2,
      color: p.ink,
      fontSize: t.sizes.base,
      paddingHorizontal: s.s3,
      paddingVertical: s.s2,
      minHeight: 40,
    },
    cardioOptionalUnit: {
      color: p.ink3,
      fontSize: t.sizes.sm,
      fontWeight: '500',
      width: 40,
    },
  });
}
